// D2-1 / D2-2 / D2-4 — THE GRANTS TAB: give somebody a tier, manage it, revoke it, and read the log.
//
// IT LIVES IN ITS OWN FILE RATHER THAN IN App.tsx, and that is not tidiness. App.tsx is at the R8
// ceiling, the ceiling ratchets down and never up, and roadmap 7 says an owner feature pays for its
// own lines instead of borrowing against them. Everything here that is a RULE — which tiers exist,
// what a duration means, when a grant is live, what the confirm sentence says — is in
// `src/accessGrants.js`, which imports nothing and is RUN by `scripts/d2-grants.mjs` every build.
// What is left in this file is a form, a table and four fetches.
//
// THE CONFIRM STEP IS THE SERVER'S OWN SENTENCE (D2-4). Nothing here composes the text of a
// confirmation. Every mutation is posted TWICE: once without `confirm`, which changes nothing and
// comes back with the exact sentence describing what would happen, and once with `confirm: true`
// after she has read that sentence. A confirm dialog whose words are written on the client can
// describe something other than what the server then does, and on the access path that is the
// whole ballgame.
// R77 — **R65 DECLARED THIS TAB ALREADY HONEST AND IT WAS HONEST ABOUT EXACTLY ONE FAILURE.** Its
// `.catch(() => setNote("The grants could not be loaded."))` fires on a THROWN request and on
// nothing else, while the two answers that actually arrive — a 500, and the `200 { ok: false }`
// this house's routes send — fell through `if (g?.ok)` with no `else` and said nothing at all. And
// R65's own check for the already-honest side was `/setNote\(/.test(file)`, a PRESENCE regex: the
// declaration was a claim, not a measurement, and this is that lesson turned on the instrument.
//
// What it cost is R73's shape, twice, and the second one is the sharpest in this round:
//   - `!grants.length` drew **"Nothing has been granted yet."** over a read that never landed, on
//     the screen she gives people access from — so the honest move is to issue the grant again.
//   - `!actions.length` drew **"Nothing recorded yet."** over the OWNER ACTION LOG, directly under
//     this file's own sentence *"Every change made from here, kept for good. Nothing on this site
//     can edit or delete a line of it."* An append-only ledger reading as empty because a query
//     failed is the one lie that record exists to make impossible (R64/R70: the failure state was
//     not a missing sentence but a wrong one).
// The mutations moved onto the shared transport in the same round as `OwnerRefunds`, because the
// two-post confirm was typed byte-for-byte in both files and the copies had already drifted (R47).
//
// Deliberately NO import of ../accessGrants.js here. The vocabulary this form offers — which tiers,
// which sources, which durations — is fetched FROM THE SERVER, which reads it out of that same
// module. So the form can never offer a choice the server would refuse, and it gets there without a
// second copy of the list living on the client.
import { useCallback, useEffect, useState } from "react";
import { loadOwnerData, ownerLoadNote, postOwnerAction, postOwnerConfirmed, ownerActionNote } from "../lib/owner-load";

type GrantRow = {
  id: string;
  email: string;
  userId: string | null;
  tier: string;
  tierLabel: string;
  source: string;
  sourceLabel: string;
  startsAt: string | null;
  endsAt: string | null;
  redeemedAt: string | null;
  revokedAt: string | null;
  note: string;
  createdBy: string;
  createdAt: string | null;
  state: "live" | "scheduled" | "expired" | "revoked";
  attachment: "redeemed" | "waiting";
};

type FormVocab = {
  tiers: Array<{ key: string; label: string }>;
  sources: Array<{ key: string; label: string }>;
  durations: Array<{ key: string; label: string; months: number | null }>;
};

type OwnerAction = {
  id: string;
  action: string;
  actorEmail: string;
  subjectEmail: string;
  note: string;
  detail: Record<string, unknown>;
  at: string;
};

// A pending mutation, held while she reads what it would do. `body` is the EXACT payload that will
// be re-posted with `confirm: true`, so the sentence she approved and the request that follows it
// cannot come apart.
type Pending = { url: string; body: Record<string, unknown>; sentence: string };

const shortDay = (value: string | null) =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }) : "—";

export function OwnerGrants({ onDenied }: { onDenied: () => void }) {
  // **`null` IS "NOT READ YET" AND `[]` IS "GENUINELY NONE"** (W5-2/R39's third state). With one
  // value for both, `useState([])` was already the empty answer and a failed read never had to
  // forge one — R73's shape, and the census this repo has been asking for.
  const [grants, setGrants] = useState<GrantRow[] | null>(null);
  const [vocab, setVocab] = useState<FormVocab | null>(null);
  const [actions, setActions] = useState<OwnerAction[] | null>(null);
  const [loading, setLoading] = useState(false);
  // TWO FACTS, TWO STATES (R66): what the READ said, and what the last ACT said. One state doing
  // both means a re-read after a grant lands erases the server's own account of what it did.
  const [loadNote, setLoadNote] = useState("");
  const [act, setAct] = useState<{ text: string; ok: boolean } | null>(null);

  // The form.
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("vet");
  const [duration, setDuration] = useState("1m");
  const [throughDate, setThroughDate] = useState("");
  const [source, setSource] = useState("owner-comp");
  const [why, setWhy] = useState("");

  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [g, a] = await Promise.all([
      loadOwnerData<{ grants: GrantRow[]; form: FormVocab }>("/api/owner/grants"),
      loadOwnerData<{ actions: OwnerAction[] }>("/api/owner/actions?limit=100"),
    ]);
    if (!g.ok && g.kind === "denied") { onDenied(); setLoading(false); return; }
    if (g.ok) { setGrants(g.data.grants); setVocab(g.data.form); }
    if (a.ok) setActions(a.data.actions);
    // **TWO ROUTES, TWO SENTENCES, JOINED — and the log's own is not borrowed from the grants'.**
    // They fail independently, and "nothing on this site can edit or delete a line of it" is a
    // promise about the LOG: a line saying the grants could not be read is not an account of it.
    setLoadNote([
      ownerLoadNote(g, "The grants could not be read just now."),
      a.ok ? "" : ownerLoadNote(a, "The owner action log could not be read just now.") || "The owner action log could not be read just now.",
    ].filter(Boolean).join(" "));
    setLoading(false);
  }, [onDenied]);

  useEffect(() => { void load(); }, [load]);

  // STEP ONE of every mutation: ask the server what this would do. It changes nothing and returns
  // the sentence. A 400 here is a validation refusal and is shown as-is.
  async function propose(url: string, body: Record<string, unknown>) {
    setBusy(true);
    setAct(null);
    const answer = await postOwnerAction<{ sentence?: string }>(url, body);
    setBusy(false);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    if (!answer.ok && answer.kind === "confirm") { setPending({ url, body, sentence: answer.sentence }); return; }
    setAct({ text: ownerActionNote(answer, "That could not be prepared. Nothing was changed."), ok: false });
  }

  // STEP TWO: the same payload, plus her confirmation — and the payload is DERIVED from step one's
  // rather than re-typed here (R31), so the sentence she approved and the request that follows it
  // cannot come apart in a spelling.
  async function commit() {
    if (!pending) return;
    setBusy(true);
    const answer = await postOwnerConfirmed<{ sentence?: string }>(pending.url, pending.body);
    setBusy(false);
    if (!answer.ok && answer.kind === "denied") { onDenied(); return; }
    // **THE SERVER'S OWN ACCOUNT OF WHAT IT DID, WHERE IT SENT ONE.** "Done, and written to the log."
    // was the house talking over three routes that each compose a sentence naming the grant, the
    // tier and the date it now ends on — the D2 law says that sentence is the server's.
    setAct(answer.ok
      ? { text: String(answer.data?.sentence || "").trim() || "Done, and written to the log.", ok: true }
      : { text: ownerActionNote(answer, "That did not go through. Nothing was changed."), ok: false });
    // R61: the confirm closes only on an act that LANDED. A refused one used to be dismissed with
    // the sentence she was reading, leaving her to fill the form in again to try it.
    if (answer.ok) { setPending(null); setEmail(""); setWhy(""); void load(); }
  }

  const needsDate = vocab?.durations.find((d) => d.key === duration)?.months === null;

  return (
    <>
      {/* ---- D2-1: THE GRANT FORM ---- */}
      <div className="owner-grant-form">
        <h3 className="owner-detail__sub">Give someone a tier</h3>
        <p className="owner-page__note">
          They are never charged for this. It works whether or not they have signed up yet — a grant to an
          address with no account waits at the door and turns on the first time they arrive.
        </p>
        <div className="owner-grant-form__row">
          <label className="owner-grant-form__field">
            <span>Email address</span>
            <input className="owner-search" type="email" value={email} autoComplete="off"
              onChange={(e) => setEmail(e.target.value)} placeholder="them@example.com" />
          </label>
          <label className="owner-grant-form__field">
            <span>Tier</span>
            <select className="owner-search" value={tier} onChange={(e) => setTier(e.target.value)}>
              {(vocab?.tiers || []).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </label>
          <label className="owner-grant-form__field">
            <span>How long</span>
            <select className="owner-search" value={duration} onChange={(e) => setDuration(e.target.value)}>
              {(vocab?.durations || []).map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          </label>
          {needsDate ? (
            <label className="owner-grant-form__field">
              <span>Through</span>
              <input className="owner-search" type="date" value={throughDate} onChange={(e) => setThroughDate(e.target.value)} />
            </label>
          ) : null}
          <label className="owner-grant-form__field">
            <span>Source</span>
            <select className="owner-search" value={source} onChange={(e) => setSource(e.target.value)}>
              {(vocab?.sources || []).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </label>
        </div>
        <label className="owner-grant-form__field owner-grant-form__field--wide">
          <span>Why (kept in the log)</span>
          <input className="owner-search" value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Optional" />
        </label>
        <button type="button" className="button" disabled={busy}
          onClick={() => propose("/api/owner/grants", { email, tier, duration, throughDate, source, note: why })}>
          Grant access
        </button>
      </div>

      {loadNote ? <p className="owner-page__note owner-fresh--failed" role="status">{loadNote}</p> : null}
      {act?.text ? <p className={act.ok ? "owner-act-note" : "owner-page__note owner-fresh--failed"} role="status">{act.text}</p> : null}

      {/* ---- D2-4: THE CONFIRM. The sentence is the SERVER'S, rendered verbatim. ---- */}
      {pending ? (
        <div className="owner-confirm" role="alertdialog" aria-live="assertive">
          <p className="owner-confirm__sentence">{pending.sentence}</p>
          <div className="owner-confirm__actions">
            <button type="button" className="button" disabled={busy} onClick={() => { void commit(); }}>Confirm</button>
            <button type="button" className="owner-link" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      ) : null}

      {/* ---- D2-2: THE GRANTS LIST ---- */}
      <h3 className="owner-detail__sub">Everything granted</h3>
      {loading ? <p className="owner-page__note">Loading...</p> : null}
      <div className="owner-table-wrap">
        {/* `--grants` lets the address column wrap. Seven nowrap columns plus a date box overflowed
            1100px and clipped "Revoke" off the right edge in the first D2-5 capture, and a control
            you cannot see is a control that does not exist. */}
        <table className="owner-table owner-table--grants">
          <thead>
            <tr><th>Who</th><th>Tier</th><th>Source</th><th>Start</th><th>End</th><th>Standing</th><th /></tr>
          </thead>
          <tbody>
            {(grants || []).map((g) => (
              <tr key={g.id} data-grant={g.id} data-grant-state={g.state}>
                {/* D6-M4 — each cell names its own column, so this table becomes CARDS at 390px
                    rather than a sideways scroll with the Standing column hiding off the edge. */}
                <td className="owner-table__email" data-label="Who">{g.email}</td>
                <td data-label="Tier">{g.tierLabel}</td>
                <td data-label="Source">{g.sourceLabel}</td>
                <td data-label="Start">{shortDay(g.startsAt)}</td>
                <td data-label="End">{shortDay(g.endsAt)}</td>
                <td data-label="Standing">
                  {/* Two independent facts, and they stay independent: what TIME says about the
                      grant, and whether it has found its member yet. A live grant that nobody has
                      signed in for is normal, not broken. */}
                  {g.state === "live" ? "Live" : g.state === "scheduled" ? "Starts later" : g.state === "expired" ? "Ended" : "Revoked"}
                  {" · "}
                  {g.attachment === "redeemed" ? "Redeemed" : "Waiting at the door"}
                </td>
                <td>
                  {g.state === "revoked" ? null : (
                    <>
                      {/* Extend OR shorten: one date box, because they are the same edit in two
                          directions and the confirm sentence says which one it is. */}
                      <span className="owner-grant-row__date">
                        <input type="date" className="owner-search owner-search--tiny"
                          aria-label={`New end date for ${g.email}`}
                          onChange={(e) => { if (e.target.value) void propose("/api/owner/grants/end", { id: g.id, throughDate: e.target.value }); }} />
                      </span>
                      <button type="button" className="owner-link" disabled={busy}
                        onClick={() => { void propose("/api/owner/grants/revoke", { id: g.id }); }}>
                        Revoke
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* The empty sentence requires a list that ARRIVED. It used to be `!grants.length` over a
          `useState([])`, so a refused read told her nobody has ever been granted anything — on the
          screen she would then use to grant it a second time. */}
      {!loading && grants && !grants.length ? <p className="owner-page__note">Nothing has been granted yet.</p> : null}

      {/* ---- D2-4: THE ACTION LOG. Append-only at the database; there is no editing door. ---- */}
      <h3 className="owner-detail__sub">Owner action log</h3>
      <p className="owner-page__note">Every change made from here, kept for good. Nothing on this site can edit or delete a line of it.</p>
      <ul className="owner-bars owner-bars--gates">
        {(actions || []).map((a) => (
          <li key={a.id} data-owner-action={a.action}>
            <span className="owner-bars__label">
              {new Date(a.at).toLocaleString()} · {a.action} · {a.subjectEmail || "—"}
              {a.note ? ` · ${a.note}` : ""}
            </span>
            <span className="owner-bars__count">{a.actorEmail}</span>
          </li>
        ))}
        {/* **THE SHARPEST ONE IN THIS ROUND.** "Nothing recorded yet." sat one line under the
            promise that nothing on this site can edit or delete a line of this log — and a failed
            query drew it. An append-only ledger reading as EMPTY is the single lie that record
            exists to make impossible, so the sentence now requires a log that arrived. */}
        {actions && !actions.length ? <li className="owner-bars__empty">Nothing recorded yet.</li> : null}
        {!loading && !actions ? <li className="owner-bars__empty">The log could not be read just now. This is not a log with nothing in it.</li> : null}
      </ul>
    </>
  );
}
